import { Dispatch, SetStateAction, useState } from "react";

import axios from "axios";
import { ValuePartExchangeFormState } from "features/vehicleDetails/modals/ValuePartExchangeForm";
import { PartExchangeModalContentResponse } from "pages/api/part-exchange-modal-content";
import { useQuery } from "react-query";
import { PartExchange } from "services/motorpoint/partExchange";
import { PartExchangeQuestionAnswersResponse } from "services/motorpoint/partExchangeQuestions";
import {
    PartExchangeQuestionAnswers,
    PartExchangeQuestions,
    getPartExchangeQuestions,
    postPartExchangeQuestionAnswers,
} from "services/motorpoint/partExchangeQuestions";
import axiosOnSuccessEvent from "utils/axiosOnSuccessEvent";

export const PRELIMINARY_APPRAISAL_COOKIE_NAME = "PreliminaryAppraisalId";

type SetPXDataFn = (
    data: PartExchange | PartExchangeQuestionAnswersResponse.AnswersResponse | undefined
) => void;

const usePartExchangeQuestions = (
    setPartExchangeData: SetPXDataFn,
    resetPartExchangeData: () => void
) => {
    const [getModalContentData, setGetModalContentData] = useState(false);

    const [tooUnique, setTooUnique] = useState<PartExchangeQuestions.TooUnique | undefined>(
        undefined
    );

    const [formAnswers, setFormAnswers] = useState<PartExchangeQuestionAnswers.Answers | undefined>(
        undefined
    );

    const [partExQuestions, setPartExQuestions] = useState<
        PartExchangeQuestions.Questions | undefined
    >(undefined);

    const [partExQuestionsVehicle, setPartExQuestionsVehicle] = useState<
        PartExchangeQuestions.CapValuation | undefined
    >(undefined);

    const resetAllQuestionsData = async () => {
        setFormAnswers(undefined);
        setPartExQuestions(undefined);
        setPartExQuestionsVehicle(undefined);
        setTooUnique(undefined);
        await resetPartExchangeData();
    };

    const isTooUnique = (
        response:
            | PartExchangeQuestions.Questions
            | PartExchangeQuestions.TooUnique
            | PartExchangeQuestionAnswersResponse.AnswersResponse
            | undefined
    ): boolean => {
        if ((response as PartExchangeQuestions.TooUnique)?.Id === "noValuationErrorMessage") {
            setTooUnique(response as PartExchangeQuestions.TooUnique);
            return true;
        }

        return false;
    };

    const getPartExQuestions = async (
        mileageReg: ValuePartExchangeFormState
    ): Promise<PartExchangeQuestions.Questions | undefined> => {
        let questions = await getPartExchangeQuestions(mileageReg);

        if (!questions || isTooUnique(questions)) return;

        setPartExQuestions(questions as PartExchangeQuestions.Questions);
        setPartExQuestionsVehicle((questions as PartExchangeQuestions.Questions).CapValuation);
        return questions as PartExchangeQuestions.Questions;
    };

    const postPartExAnswers = async (
        answers: PartExchangeQuestionAnswers.Answers
    ): Promise<PartExchangeQuestionAnswersResponse.AnswersResponse | undefined> => {
        if (!answers) return undefined;

        const answersResponse = await postPartExchangeQuestionAnswers(answers);

        if (!answersResponse || isTooUnique(answersResponse)) return;

        setPartExchangeData(answersResponse as PartExchangeQuestionAnswersResponse.AnswersResponse);

        return answersResponse as PartExchangeQuestionAnswersResponse.AnswersResponse;
    };

    const updateFormAnswers = (
        section: "UserAnswers" | "PersonalDetailsModel",
        answers:
            | Partial<PartExchangeQuestionAnswers.PersonalDetailsModel>
            | Partial<PartExchangeQuestionAnswers.UserAnswers>
    ): PartExchangeQuestionAnswers.Answers => {
        const newAnswers = {
            ...(!!formAnswers && { ...formAnswers }),
            [section]: {
                ...(!!formAnswers?.[section] && formAnswers?.[section]),
                ...answers,
            },
        } as PartExchangeQuestionAnswers.Answers;

        setFormAnswers(newAnswers);
        return newAnswers;
    };

    const getModalContentRequest = async (): Promise<
        PartExchangeModalContentResponse | undefined
    > => {
        try {
            const response = await axios.get<PartExchangeModalContentResponse>(
                "/api/part-exchange-modal-content"
            );
            axiosOnSuccessEvent(response);
            return response.data;
        } catch {
            return;
        }
    };

    const { data: modalContent } = useQuery({
        refetchOnWindowFocus: false,
        queryKey: "part-exchange:modal-content",
        queryFn: getModalContentRequest,
        retry: 3,
        retryDelay: (attemptIndex, error) => {
            return Math.pow(2, attemptIndex) * 200;
        },
        keepPreviousData: true,
        enabled: getModalContentData,
    });

    return {
        setGetModalContentData,
        postPartExAnswers,
        getPartExQuestions,
        updateFormAnswers,
        resetAllQuestionsData,
        partExQuestions,
        partExQuestionsVehicle,
        formAnswers,
        tooUnique,
        modalContent,
    };
};

export default usePartExchangeQuestions;
